import { useCallback, useEffect, useRef, useState } from "react";
import {
  Bell,
  CirclePlus,
  Download,
  Eye,
  FileText,
  LoaderCircle,
  Printer,
  Search,
  Share2,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { getError } from "@/api";
import { CatalogueImageSearch } from "@/components/CatalogueImageSearch";

export default function ProductCataloguePage() {
  const [products, setProducts] = useState([]);
  const [productCounts, setProductCounts] = useState({ active: 0, inactive: 0 });
  const [newOnly, setNewOnly] = useState(false);
  const [newDismissed, setNewDismissed] = useState(() => (
    localStorage.getItem("catalogue-new-products-seen") === "true"
  ));
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pdfSharing, setPdfSharing] = useState(false);
  const [imageSearch, setImageSearch] = useState(null);
  const pdfCache = useRef(new Map());
  const pdfRequests = useRef(new Map());

  const load = useCallback(() => {
    return api.get("/catalogue/products", { params: { search } })
      .then((response) => setProducts(response.data))
      .catch((error) => toast.error(getError(error)));
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/products")
      .then((response) => setProductCounts({
        active: response.data.filter((product) => product.active).length,
        inactive: response.data.filter((product) => !product.active).length,
      }))
      .catch(() => {});
  }, []);

  const getPdfUrl = useCallback(async (searchValue) => {
    const cachedUrl = pdfCache.current.get(searchValue);
    if (cachedUrl) {
      return cachedUrl;
    }

    const pendingRequest = pdfRequests.current.get(searchValue);
    if (pendingRequest) {
      return pendingRequest;
    }

    const request = api.get("/catalogue/pdf", {
      params: { search: searchValue },
      responseType: "blob",
    }).then((response) => {
      const objectUrl = URL.createObjectURL(response.data);
      pdfCache.current.set(searchValue, objectUrl);
      return objectUrl;
    }).finally(() => {
      pdfRequests.current.delete(searchValue);
    });
    pdfRequests.current.set(searchValue, request);

    return request;
  }, []);

  useEffect(() => {
    const prefetchTimer = window.setTimeout(() => {
      getPdfUrl("").catch(() => {});
    }, 0);

    return () => window.clearTimeout(prefetchTimer);
  }, [getPdfUrl]);

  useEffect(() => {
    return () => {
      pdfCache.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const generatePdf = async () => {
    if (pdfLoading || pdfDownloading || pdfSharing) {
      return;
    }

    setPdfLoading(true);

    try {
      setPdfUrl(await getPdfUrl(search));
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (pdfLoading || pdfDownloading || pdfSharing) {
      return;
    }

    setPdfDownloading(true);

    try {
      const url = await getPdfUrl(search);
      const link = document.createElement("a");
      link.href = url;
      link.download = "catalogue-produits.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Téléchargement du catalogue lancé.");
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setPdfDownloading(false);
    }
  };

  const sharePdf = async () => {
    if (pdfLoading || pdfDownloading || pdfSharing) {
      return;
    }

    setPdfSharing(true);

    try {
      const url = await getPdfUrl(search);
      const response = await fetch(url);
      const pdfFile = new File(
        [await response.blob()],
        "catalogue-produits.pdf",
        { type: "application/pdf" },
      );

      if (navigator.canShare?.({ files: [pdfFile] })) {
        await navigator.share({
          title: "Catalogue produits",
          text: "Catalogue produits Ecom Guinée",
          files: [pdfFile],
        });
        toast.success("Catalogue prêt à être partagé.");
        return;
      }

      const link = document.createElement("a");
      link.href = url;
      link.download = pdfFile.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.info("Partage non disponible : le PDF a été téléchargé.");
    } catch (error) {
      if (error.name !== "AbortError") {
        toast.error(getError(error));
      }
    } finally {
      setPdfSharing(false);
    }
  };

  const newProducts = products.filter((product) => (
    product.created_at && Date.now() - new Date(product.created_at).getTime() <= 7 * 86400000
  ));
  const visibleProducts = imageSearch?.products || (newOnly ? newProducts : products);

  const viewNew = () => {
    setNewOnly(true);
    setImageSearch(null);
    setNewDismissed(true);
    localStorage.setItem("catalogue-new-products-seen", "true");
  };

  const handleImageResults = (results) => {
    setNewOnly(false);
    setImageSearch(results);
  };

  const clearImageResults = () => setImageSearch(null);

  return (
    <div className="page-enter" data-testid="product-catalogue-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">CATALOGUE PRODUITS</p>
          <h1>Découvrez nos produits</h1>
          <p>Produits actifs, disponibles à la consultation et à la présentation.</p>
        </div>
        <div className="catalogue-pdf-header-actions" data-testid="catalogue-pdf-header-actions">
          <button
            className="secondary-action"
            data-testid="share-product-catalogue-direct-button"
            disabled={pdfLoading || pdfDownloading || pdfSharing}
            onClick={sharePdf}
            type="button"
          >
            {pdfSharing ? <LoaderCircle className="dashboard-spinner" size={17} /> : <Share2 size={17} />}
            {pdfSharing ? "Partage…" : "Partager"}
          </button>
          <button
            className="secondary-action"
            data-testid="download-product-catalogue-direct-button"
            disabled={pdfLoading || pdfDownloading || pdfSharing}
            onClick={downloadPdf}
            type="button"
          >
            {pdfDownloading ? (
              <LoaderCircle className="dashboard-spinner" size={17} />
            ) : (
              <Download size={17} />
            )}
            {pdfDownloading ? "Téléchargement…" : "Télécharger"}
          </button>
          <button
            className="primary-action"
            data-testid="generate-product-catalogue-button"
            disabled={pdfLoading || pdfDownloading || pdfSharing}
            onClick={generatePdf}
            type="button"
          >
            {pdfLoading ? (
              <LoaderCircle className="dashboard-spinner" size={17} />
            ) : (
              <FileText size={17} />
            )}
            {pdfLoading ? "Préparation…" : "Catalogue PDF"}
          </button>
        </div>
      </div>
      {newProducts.length > 0 && !newDismissed && (
        <button
          className="catalogue-new-alert"
          data-testid="new-products-alert"
          onClick={viewNew}
          type="button"
        >
          <Bell size={17} /> {newProducts.length} nouveau(x) produit(s) cette semaine
        </button>
      )}
      <div className="quick-search-bar" data-testid="catalogue-product-counts">
        <span data-testid="catalogue-active-products-count">
          Produits actifs : {productCounts.active}
        </span>
        <span data-testid="catalogue-inactive-products-count">
          Produits inactifs : {productCounts.inactive}
        </span>
        {newProducts.length > 0 && (
          <button
            className="catalogue-new-count-button"
            data-testid="show-new-products-button"
            onClick={() => {
              setNewOnly(!newOnly);
              setImageSearch(null);
            }}
            type="button"
          >
            {newOnly ? "Tous les produits" : `Nouveautés 7 jours : ${newProducts.length}`}
          </button>
        )}
      </div>
      <div className="filters-bar" data-testid="catalogue-text-search-bar">
        <div className="filter-search">
          <Search aria-hidden="true" size={17} />
          <input
            data-testid="catalogue-search-input"
            onChange={(event) => {
              setSearch(event.target.value);
              setImageSearch(null);
            }}
            onKeyDown={(event) => event.key === "Enter" && load()}
            placeholder="Rechercher par nom ou tag"
            value={search}
          />
        </div>
        <button
          className="secondary-action"
          data-testid="catalogue-search-button"
          onClick={load}
          type="button"
        >
          Rechercher
        </button>
      </div>
      <CatalogueImageSearch onResults={handleImageResults} />
      {imageSearch && (
        <section className="catalogue-image-result" data-testid="catalogue-image-search-results">
          <div>
            <p className="eyebrow">ANALYSE DE LA PHOTO</p>
            <strong data-testid="catalogue-image-search-summary">{imageSearch.summary}</strong>
            <span data-testid="catalogue-image-search-keywords">
              {imageSearch.keywords.join(" · ")}
            </span>
          </div>
          <button
            className="secondary-action"
            data-testid="catalogue-image-search-clear-results-button"
            onClick={clearImageResults}
            type="button"
          >
            <X size={16} /> Effacer les résultats
          </button>
        </section>
      )}
      <div className="catalogue-products-grid" data-testid="catalogue-products-grid">
        {visibleProducts.map((product) => (
          <article
            className="catalogue-product-card"
            key={product.product_id}
          >
            <button
              className="catalogue-product-card-main"
              data-testid={`catalogue-product-${product.product_id}`}
              onClick={() => setSelected(product)}
              type="button"
            >
              <div className="catalogue-product-image">
                {product.photo_file_id ? (
                  <img
                    alt={product.name}
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/products/${product.product_id}/photo`}
                  />
                ) : (
                  <FileText aria-label="Photo indisponible" size={26} />
                )}
              </div>
              <div>
                <span>{product.name}</span>
                <strong>{product.sale_price.toLocaleString("fr-FR")} GNF</strong>
              </div>
              <Eye size={17} />
            </button>
            {imageSearch && (
              <div
                className="catalogue-image-result-actions"
                data-testid={`catalogue-image-result-actions-${product.product_id}`}
              >
                <button
                  data-testid={`catalogue-image-result-open-${product.product_id}`}
                  onClick={() => setSelected(product)}
                  type="button"
                >
                  <Eye size={15} /> Fiche
                </button>
                <Link
                  className="primary-action"
                  data-testid={`catalogue-image-result-prepare-movement-${product.product_id}`}
                  to={`/mouvements?product_id=${encodeURIComponent(product.product_id)}`}
                >
                  <CirclePlus size={15} /> Préparer un mouvement
                </Link>
              </div>
            )}
          </article>
        ))}
      </div>
      {!visibleProducts.length && (
        <div className="empty-inline" data-testid="empty-catalogue-products-message">
          {imageSearch
            ? "Aucun produit actif ne correspond à cette image."
            : "Aucun produit actif ne correspond à votre recherche."}
        </div>
      )}
      {selected && <ProductDetail product={selected} onClose={() => setSelected(null)} />}
      {pdfUrl && <PdfViewer onClose={() => setPdfUrl("")} onShare={sharePdf} url={pdfUrl} />}
    </div>
  );
}

function ProductDetail({ product, onClose }) {
  return (
    <div className="document-modal" data-testid="catalogue-product-detail-modal">
      <div className="catalogue-preview-window catalogue-detail-window">
        <header>
          <div>
            <p className="eyebrow">FICHE CATALOGUE</p>
            <h2>{product.name}</h2>
          </div>
          <button
            aria-label="Fermer la fiche produit"
            data-testid="close-product-detail-button"
            onClick={onClose}
            type="button"
          >
            <X size={18} />
          </button>
        </header>
        <div className="catalogue-detail-layout" data-testid="catalogue-product-detail-content">
          <div className="catalogue-detail-photo">
            {product.photo_file_id ? (
              <img
                alt={product.name}
                className="detail-product-photo"
                src={`${process.env.REACT_APP_BACKEND_URL}/api/products/${product.product_id}/photo`}
              />
            ) : (
              <FileText aria-label="Photo indisponible" size={34} />
            )}
          </div>
          <div className="catalogue-detail-info">
            <div>
              <span>Catégorie</span>
              <strong>{product.category_name || "Sans catégorie"}</strong>
            </div>
            <div>
              <span>Prix de vente</span>
              <strong className="catalogue-detail-price">
                {product.sale_price.toLocaleString("fr-FR")} GNF
              </strong>
            </div>
            <div className="catalogue-detail-wide">
              <span>Description</span>
              <p>{product.description || "Description non renseignée."}</p>
            </div>
            <div className="catalogue-detail-wide">
              <span>Mode d’emploi</span>
              <p>{product.usage_instructions || "Non renseigné."}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PdfViewer({ onClose, onShare, url }) {
  const frameRef = useRef(null);

  const printPdf = () => {
    frameRef.current?.contentWindow?.print();
  };

  return (
    <div className="document-modal" data-testid="catalogue-pdf-modal">
      <div className="catalogue-preview-window">
        <header>
          <h2>Catalogue produits</h2>
          <button
            aria-label="Fermer l’aperçu PDF"
            data-testid="close-catalogue-pdf-button"
            onClick={onClose}
            type="button"
          >
            <X size={18} />
          </button>
        </header>
        <iframe ref={frameRef} src={url} title="Catalogue produits" />
        <footer>
          <a data-testid="download-catalogue-pdf-button" download="catalogue-produits.pdf" href={url}>
            <Download size={16} /> PDF
          </a>
          <button data-testid="print-catalogue-button" onClick={printPdf} type="button">
            <Printer size={16} /> Imprimer
          </button>
          <button data-testid="share-catalogue-pdf-button" onClick={onShare} type="button">
            <Share2 size={16} /> Partager
          </button>
        </footer>
      </div>
    </div>
  );
}